import { useEffect, useMemo, useState } from "react";
import { exportRowsToExcel } from "../../../utils/excel";

const INITIAL_PROJECT_ROW_FILTERS = {
  projectName: "",
  projectCode: "",
  createdAt: "",
  workflowName: "",
  clientName: "",
  managerName: "",
  startDate: "",
  endDate: "",
  minDurationHours: "",
  minProjectAmount: "",
  minInvoiceTotal: "",
  minCollectedAmount: "",
  minRemainingCollection: "",
  minRealizedCost: "",
  minRemainingEstimatedCost: "",
  minTotalEstimatedCost: "",
  minNetProfit: "",
  minEstimatedNetProfit: "",
};

export const useProjectsTabModel = ({
  mainTab,
  projects,
  projectCollections,
  users,
  canViewCosts,
  workflowText,
  t,
  formatHoursTr,
  getPlannedHours,
  getProjectFinanceSummaryForDashboard,
  getProjectCode,
  getApplicableAverageRate,
  projectRealizedCostByWorkflowId,
  getProjectCurrencyForFormData,
  convertAmountToTry,
  convertTryAmountToCurrency,
}: any) => {
  const [projectSearchTerm, setProjectSearchTerm] = useState("");
  const [projectWorkflowFilter, setProjectWorkflowFilter] = useState("all");
  const [projectArchiveFilter, setProjectArchiveFilter] = useState("all");
  const [projectClientFilter, setProjectClientFilter] = useState("all");
  const [projectManagerFilter, setProjectManagerFilter] = useState("all");
  const [projectStartDate, setProjectStartDate] = useState("");
  const [projectEndDate, setProjectEndDate] = useState("");
  const [projectRowFilters, setProjectRowFilters] = useState(
    INITIAL_PROJECT_ROW_FILTERS,
  );
  const [projectCurrentPage, setProjectCurrentPage] = useState(1);
  const [projectItemsPerPage, setProjectItemsPerPage] = useState(1000);
  const [projectSummaryMode, setProjectSummaryMode] = useState("total");
  const [projectSortField, setProjectSortField] = useState("startedAt");
  const [projectSortOrder, setProjectSortOrder] = useState<
    "asc" | "desc" | null
  >("desc");

  useEffect(() => {
    if (mainTab !== "projects") return;

    const syncedWorkflowName =
      projectWorkflowFilter !== "all" ? projectWorkflowFilter : "";
    const syncedClientName =
      projectClientFilter !== "all" ? projectClientFilter : "";
    const syncedManagerName =
      projectManagerFilter !== "all" ? projectManagerFilter : "";

    setProjectRowFilters((prev) => ({
      ...prev,
      workflowName: syncedWorkflowName,
      clientName: syncedClientName,
      managerName: syncedManagerName,
      startDate: projectStartDate || "",
      endDate: projectEndDate || "",
    }));
  }, [
    mainTab,
    projectWorkflowFilter,
    projectClientFilter,
    projectManagerFilter,
    projectStartDate,
    projectEndDate,
  ]);

  const handleProjectSort = (field: string) => {
    if (projectSortField === field) {
      if (projectSortOrder === "asc") {
        setProjectSortOrder("desc");
      } else if (projectSortOrder === "desc") {
        setProjectSortOrder(null);
      } else {
        setProjectSortOrder("asc");
      }
      return;
    }

    setProjectSortField(field);
    setProjectSortOrder("asc");
  };

  const normalizedProjects = useMemo(
    () =>
      projects.map((project: any) => {
        let formData: Record<string, any> = {};
        try {
          formData = project.workflowContainerFormData
            ? typeof project.workflowContainerFormData === "string"
              ? JSON.parse(project.workflowContainerFormData)
              : project.workflowContainerFormData
            : {};
        } catch {
          formData = {};
        }

        const projectName = formData.name || formData.projectName || "";
        const clientName = formData.clientName || formData.client || "";
        const managerId = formData.projectManager;
        const manager = users.find((u: any) => u.id === managerId);
        const managerName = manager
          ? `${manager.firstName} ${manager.lastName}`
          : "";
        const projectCode = getProjectCode(project, formData);
        const createdAtValue = project.createdAt
          ? new Date(project.createdAt).toISOString().slice(0, 10)
          : "";
        const projectStartDateValue = formData.startDate
          ? new Date(formData.startDate).toISOString().slice(0, 10)
          : "";
        const projectEndDateValue = formData.endDate
          ? new Date(formData.endDate).toISOString().slice(0, 10)
          : "";
        const rawSpentHours = Number(project.totalSpentHours);
        const spentHours =
          Number.isFinite(rawSpentHours) && rawSpentHours > 0 ? rawSpentHours : 0;

        return {
          project,
          formData,
          projectName,
          clientName,
          managerName,
          projectCode,
          archiveStatus:
            String(project?.status || "").toUpperCase() === "ARCHIVED"
              ? "archived"
              : "active",
          createdAtValue,
          workflowNameValue: project.templateWorkflowName || "",
          projectStartDateValue,
          projectEndDateValue,
          spentHours,
          searchBlob: [
            projectName,
            clientName,
            managerName,
            projectCode,
            JSON.stringify(formData || {}),
          ]
            .join(" ")
            .toLocaleLowerCase("tr-TR"),
        };
      }),
    [getProjectCode, projects, users],
  );

  const currentAverageHourlyRate = useMemo(
    () => getApplicableAverageRate(new Date()),
    [getApplicableAverageRate],
  );

  const collectionTotalsTryByWorkflowInstanceId = useMemo(() => {
    const totals = new Map<number, number>();

    if (!canViewCosts) {
      return totals;
    }

    (projectCollections || []).forEach((collection: any) => {
      const workflowInstanceId = Number(collection?.workflowInstanceId);
      if (!Number.isFinite(workflowInstanceId) || workflowInstanceId <= 0) {
        return;
      }

      const amountTry = convertAmountToTry(
        Number(collection?.amount),
        String(collection?.currency || "TRY"),
      );

      if (amountTry === null) {
        return;
      }

      totals.set(workflowInstanceId, (totals.get(workflowInstanceId) ?? 0) + amountTry);
    });

    return totals;
  }, [canViewCosts, convertAmountToTry, projectCollections]);

  const filteredProjects = useMemo(() => {
    const activeAverageHourlyRate = canViewCosts
      ? getApplicableAverageRate(new Date())
      : null;

    const getFinancialMetrics = (item: any) => {
      if (!canViewCosts) {
        return {
          projectAmount: null,
          vatAmount: null,
          invoiceTotal: null,
          totalCollected: null,
          remainingCollection: null,
          realizedCost: null,
          remainingEstimatedCost: null,
          totalEstimatedCost: null,
          netProfit: null,
          estimatedNetProfit: null,
        };
      }

      const plannedHours = getPlannedHours(item.formData);
      const projectCurrency = getProjectCurrencyForFormData(item.formData);
      const remainingHours =
        plannedHours > 0 ? Math.max(plannedHours - item.spentHours, 0) : 0;
      const realizedCostTry =
        projectRealizedCostByWorkflowId.get(item.project.id) ?? 0;
      const totalCollectedTry =
        collectionTotalsTryByWorkflowInstanceId.get(Number(item.project.id)) ?? null;
      const remainingEstimatedCostTry =
        activeAverageHourlyRate !== null
          ? remainingHours * activeAverageHourlyRate
          : null;
      const realizedCost = convertTryAmountToCurrency(
        realizedCostTry,
        projectCurrency,
      );
      const remainingEstimatedCost =
        remainingEstimatedCostTry !== null
          ? convertTryAmountToCurrency(
              remainingEstimatedCostTry,
              projectCurrency,
            )
          : null;
      const totalEstimatedCost =
        realizedCost !== null && remainingEstimatedCost !== null
          ? realizedCost + remainingEstimatedCost
          : null;
      const financeSummary = getProjectFinanceSummaryForDashboard(
        item.formData,
        realizedCostTry,
        remainingEstimatedCostTry !== null
          ? realizedCostTry + remainingEstimatedCostTry
          : null,
        totalCollectedTry,
      );

      return {
        projectAmount: financeSummary.projectAmount,
        vatAmount:
          financeSummary.projectAmount !== null &&
          financeSummary.invoiceTotal !== null
            ? financeSummary.invoiceTotal - financeSummary.projectAmount
            : null,
        invoiceTotal: financeSummary.invoiceTotal,
        totalCollected: financeSummary.totalCollected,
        remainingCollection: financeSummary.remainingCollection,
        realizedCost,
        remainingEstimatedCost,
        totalEstimatedCost,
        netProfit: financeSummary.netProfit,
        estimatedNetProfit: financeSummary.estimatedNetProfit,
      };
    };

    const filtered = normalizedProjects
      .filter((item: any) => {
        if (projectSearchTerm) {
          const searchLower = projectSearchTerm.toLocaleLowerCase("tr-TR");
          if (!item.searchBlob.includes(searchLower)) {
            return false;
          }
        }

        if (
          projectWorkflowFilter !== "all" &&
          item.project.templateWorkflowName !== projectWorkflowFilter
        ) {
          return false;
        }

        if (
          projectArchiveFilter !== "all" &&
          item.archiveStatus !== projectArchiveFilter
        ) {
          return false;
        }

        if (
          projectClientFilter !== "all" &&
          item.clientName !== projectClientFilter
        ) {
          return false;
        }

        if (
          projectManagerFilter !== "all" &&
          item.managerName !== projectManagerFilter
        ) {
          return false;
        }

        if (
          projectStartDate &&
          item.formData.startDate &&
          new Date(item.formData.startDate) < new Date(projectStartDate)
        ) {
          return false;
        }

        if (
          projectEndDate &&
          item.formData.endDate &&
          new Date(item.formData.endDate) > new Date(projectEndDate)
        ) {
          return false;
        }

        const rf = projectRowFilters;

        if (
          rf.projectName.trim() &&
          !item.projectName.toLocaleLowerCase("tr-TR").includes(
            rf.projectName.trim().toLocaleLowerCase("tr-TR"),
          )
        ) {
          return false;
        }
        if (
          rf.projectCode.trim() &&
          !item.projectCode.toLocaleLowerCase("tr-TR").includes(
            rf.projectCode.trim().toLocaleLowerCase("tr-TR"),
          )
        ) {
          return false;
        }
        if (rf.createdAt && item.createdAtValue !== rf.createdAt) {
          return false;
        }
        if (
          rf.workflowName.trim() &&
          !item.workflowNameValue.toLocaleLowerCase("tr-TR").includes(
            rf.workflowName.trim().toLocaleLowerCase("tr-TR"),
          )
        ) {
          return false;
        }
        if (
          rf.clientName.trim() &&
          !item.clientName.toLocaleLowerCase("tr-TR").includes(
            rf.clientName.trim().toLocaleLowerCase("tr-TR"),
          )
        ) {
          return false;
        }
        if (
          rf.managerName.trim() &&
          !item.managerName.toLocaleLowerCase("tr-TR").includes(
            rf.managerName.trim().toLocaleLowerCase("tr-TR"),
          )
        ) {
          return false;
        }
        if (rf.startDate && item.projectStartDateValue < rf.startDate) {
          return false;
        }
        if (rf.endDate && item.projectEndDateValue > rf.endDate) {
          return false;
        }
        if (rf.minDurationHours) {
          const minDuration = Number.parseFloat(rf.minDurationHours);
          if (Number.isFinite(minDuration) && item.spentHours < minDuration) {
            return false;
          }
        }

        if (canViewCosts) {
          const financialMetrics = getFinancialMetrics(item);

          if (
            rf.minProjectAmount &&
            (financialMetrics.projectAmount === null ||
              financialMetrics.projectAmount <
                Number.parseFloat(rf.minProjectAmount))
          ) {
            return false;
          }
          if (
            rf.minRealizedCost &&
            (financialMetrics.realizedCost === null ||
              financialMetrics.realizedCost <
                Number.parseFloat(rf.minRealizedCost))
          ) {
            return false;
          }
          if (
            rf.minInvoiceTotal &&
            (financialMetrics.invoiceTotal === null ||
              financialMetrics.invoiceTotal <
                Number.parseFloat(rf.minInvoiceTotal))
          ) {
            return false;
          }
          if (
            rf.minCollectedAmount &&
            (financialMetrics.totalCollected === null ||
              financialMetrics.totalCollected <
                Number.parseFloat(rf.minCollectedAmount))
          ) {
            return false;
          }
          if (
            rf.minRemainingCollection &&
            (financialMetrics.remainingCollection === null ||
              financialMetrics.remainingCollection <
                Number.parseFloat(rf.minRemainingCollection))
          ) {
            return false;
          }
          if (
            rf.minRemainingEstimatedCost &&
            (financialMetrics.remainingEstimatedCost === null ||
              financialMetrics.remainingEstimatedCost <
                Number.parseFloat(rf.minRemainingEstimatedCost))
          ) {
            return false;
          }
          if (
            rf.minTotalEstimatedCost &&
            (financialMetrics.totalEstimatedCost === null ||
              financialMetrics.totalEstimatedCost <
                Number.parseFloat(rf.minTotalEstimatedCost))
          ) {
            return false;
          }
          if (
            rf.minNetProfit &&
            (financialMetrics.netProfit === null ||
              financialMetrics.netProfit < Number.parseFloat(rf.minNetProfit))
          ) {
            return false;
          }
          if (
            rf.minEstimatedNetProfit &&
            (financialMetrics.estimatedNetProfit === null ||
              financialMetrics.estimatedNetProfit <
                Number.parseFloat(rf.minEstimatedNetProfit))
          ) {
            return false;
          }
        }

        return true;
      });

     if (!projectSortOrder) {
      return filtered.map((item: any) => item.project);
    }

    return [...filtered]
      .sort((aItem: any, bItem: any) => {
        const a = aItem.project;
        const b = bItem.project;
        const aFormData = aItem.formData || {};
        const bFormData = bItem.formData || {};

        let aValue: any = 0;
        let bValue: any = 0;

        if (projectSortField === "workflowContainerName") {
          aValue = (a.workflowContainerName || "").toLowerCase();
          bValue = (b.workflowContainerName || "").toLowerCase();
        } else if (projectSortField === "templateWorkflowName") {
          aValue = (a.templateWorkflowName || "").toLowerCase();
          bValue = (b.templateWorkflowName || "").toLowerCase();
        } else if (projectSortField === "clientName") {
          aValue = (
            aFormData.clientName ||
            aFormData.client ||
            ""
          ).toLowerCase();
          bValue = (
            bFormData.clientName ||
            bFormData.client ||
            ""
          ).toLowerCase();
        } else if (projectSortField === "projectCode") {
          aValue = getProjectCode(a, aFormData).toLowerCase();
          bValue = getProjectCode(b, bFormData).toLowerCase();
        } else if (projectSortField === "createdAt") {
          aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        } else if (projectSortField === "projectManager") {
          aValue = (aItem.managerName || "").toLowerCase();
          bValue = (bItem.managerName || "").toLowerCase();
        } else if (projectSortField === "startDate") {
          aValue = aFormData.startDate
            ? new Date(aFormData.startDate).getTime()
            : 0;
          bValue = bFormData.startDate
            ? new Date(bFormData.startDate).getTime()
            : 0;
        } else if (projectSortField === "endDate") {
          aValue = aFormData.endDate ? new Date(aFormData.endDate).getTime() : 0;
          bValue = bFormData.endDate ? new Date(bFormData.endDate).getTime() : 0;
        } else if (projectSortField === "startedAt") {
          aValue = a.startedAt ? new Date(a.startedAt).getTime() : 0;
          bValue = b.startedAt ? new Date(b.startedAt).getTime() : 0;
        } else if (projectSortField === "totalSpentHours") {
          aValue = Number(a.totalSpentHours ?? 0);
          bValue = Number(b.totalSpentHours ?? 0);
        } else if (projectSortField === "plannedHours") {
          aValue = Number(getPlannedHours(aFormData) ?? 0);
          bValue = Number(getPlannedHours(bFormData) ?? 0);
        } else if (projectSortField === "remainingHours") {
          const aPlannedHours = getPlannedHours(aFormData);
          const bPlannedHours = getPlannedHours(bFormData);
          const aSpentHours = Number(a.totalSpentHours ?? 0);
          const bSpentHours = Number(b.totalSpentHours ?? 0);
          aValue =
            aPlannedHours > 0 && Number.isFinite(aSpentHours)
              ? aPlannedHours - aSpentHours
              : 0;
          bValue =
            bPlannedHours > 0 && Number.isFinite(bSpentHours)
              ? bPlannedHours - bSpentHours
              : 0;
        } else if (canViewCosts) {
          const aFinancialMetrics = getFinancialMetrics(aItem);
          const bFinancialMetrics = getFinancialMetrics(bItem);

          if (projectSortField === "realizedCost") {
            aValue = Number(aFinancialMetrics.realizedCost ?? 0);
            bValue = Number(bFinancialMetrics.realizedCost ?? 0);
          } else if (projectSortField === "projectAmount") {
            aValue = Number(aFinancialMetrics.projectAmount ?? 0);
            bValue = Number(bFinancialMetrics.projectAmount ?? 0);
          } else if (projectSortField === "vatAmount") {
            aValue = Number(aFinancialMetrics.vatAmount ?? 0);
            bValue = Number(bFinancialMetrics.vatAmount ?? 0);
          } else if (projectSortField === "invoiceTotal") {
            aValue = Number(aFinancialMetrics.invoiceTotal ?? 0);
            bValue = Number(bFinancialMetrics.invoiceTotal ?? 0);
          } else if (projectSortField === "totalCollected") {
            aValue = Number(aFinancialMetrics.totalCollected ?? 0);
            bValue = Number(bFinancialMetrics.totalCollected ?? 0);
          } else if (projectSortField === "remainingCollection") {
            aValue = Number(aFinancialMetrics.remainingCollection ?? 0);
            bValue = Number(bFinancialMetrics.remainingCollection ?? 0);
          } else if (projectSortField === "remainingEstimatedCost") {
            aValue = Number(aFinancialMetrics.remainingEstimatedCost ?? 0);
            bValue = Number(bFinancialMetrics.remainingEstimatedCost ?? 0);
          } else if (projectSortField === "totalEstimatedCost") {
            aValue = Number(aFinancialMetrics.totalEstimatedCost ?? 0);
            bValue = Number(bFinancialMetrics.totalEstimatedCost ?? 0);
          } else if (projectSortField === "netProfit") {
            aValue = Number(aFinancialMetrics.netProfit ?? 0);
            bValue = Number(bFinancialMetrics.netProfit ?? 0);
          } else if (projectSortField === "estimatedNetProfit") {
            aValue = Number(aFinancialMetrics.estimatedNetProfit ?? 0);
            bValue = Number(bFinancialMetrics.estimatedNetProfit ?? 0);
          }
        }

        if (aValue < bValue) return projectSortOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return projectSortOrder === "asc" ? 1 : -1;
        return 0;
      })
      .map((item: any) => item.project);
  }, [
    canViewCosts,
    getApplicableAverageRate,
    getPlannedHours,
    getProjectCode,
    getProjectCurrencyForFormData,
    getProjectFinanceSummaryForDashboard,
    normalizedProjects,
    projectClientFilter,
    projectEndDate,
    projectManagerFilter,
    projectRealizedCostByWorkflowId,
    projectRowFilters,
    projectSearchTerm,
    projectSortField,
    projectSortOrder,
    projectStartDate,
    projectArchiveFilter,
    projectWorkflowFilter,
    collectionTotalsTryByWorkflowInstanceId,
    convertAmountToTry,
    convertTryAmountToCurrency,
  ]);

  useEffect(() => {
    setProjectCurrentPage(1);
  }, [
    projectSearchTerm,
    projectWorkflowFilter,
    projectArchiveFilter,
    projectClientFilter,
    projectManagerFilter,
    projectStartDate,
    projectEndDate,
    projectRowFilters,
    projectItemsPerPage,
  ]);

  const isShowAllProjects = projectItemsPerPage <= 0;
  const projectPageStartIndex = isShowAllProjects
    ? 0
    : (projectCurrentPage - 1) * projectItemsPerPage;
  const projectTotalPages = isShowAllProjects
    ? 1
    : Math.max(1, Math.ceil(filteredProjects.length / projectItemsPerPage));
  const paginatedProjects = isShowAllProjects
    ? filteredProjects
    : filteredProjects.slice(
        projectPageStartIndex,
        projectPageStartIndex + projectItemsPerPage,
      );

  const filteredProjectsTotalSpentDuration = useMemo(
    () =>
      filteredProjects.reduce((sum: number, project: any) => {
        const rawSpentHours = Number(project.totalSpentHours);
        const spentHours =
          Number.isFinite(rawSpentHours) && rawSpentHours > 0 ? rawSpentHours : 0;
        return sum + spentHours;
      }, 0),
    [filteredProjects],
  );

  const filteredProjectsAverageSpentDuration =
    filteredProjects.length > 0
      ? filteredProjectsTotalSpentDuration / filteredProjects.length
      : 0;

  const filteredProjectsTotalPlannedDuration = useMemo(
    () =>
      filteredProjects.reduce((sum: number, project: any) => {
        const formData =
          typeof project.workflowContainerFormData === "string"
            ? JSON.parse(project.workflowContainerFormData || "{}")
            : project.workflowContainerFormData || {};
        const plannedHours = getPlannedHours(formData);
        return sum + (Number.isFinite(plannedHours) && plannedHours > 0 ? plannedHours : 0);
      }, 0),
    [filteredProjects, getPlannedHours],
  );

  const filteredProjectsAveragePlannedDuration =
    filteredProjects.length > 0
      ? filteredProjectsTotalPlannedDuration / filteredProjects.length
      : 0;

  const filteredProjectsTotalRemainingDuration = useMemo(
    () =>
      filteredProjects.reduce((sum: number, project: any) => {
        const formData =
          typeof project.workflowContainerFormData === "string"
            ? JSON.parse(project.workflowContainerFormData || "{}")
            : project.workflowContainerFormData || {};
        const plannedHours = getPlannedHours(formData);
        const spentHours = Number(project.totalSpentHours ?? 0);
        if (!(Number.isFinite(plannedHours) && plannedHours > 0)) {
          return sum;
        }
        return sum + (plannedHours - (Number.isFinite(spentHours) ? spentHours : 0));
      }, 0),
    [filteredProjects, getPlannedHours],
  );

  const filteredProjectsAverageRemainingDuration =
    filteredProjects.length > 0
      ? filteredProjectsTotalRemainingDuration / filteredProjects.length
      : 0;

  const filteredProjectsTotalRealizedCost = useMemo(() => {
    if (!canViewCosts) return 0;
    return filteredProjects.reduce(
      (sum: number, project: any) =>
        sum + (projectRealizedCostByWorkflowId.get(project.id) ?? 0),
      0,
    );
  }, [canViewCosts, filteredProjects, projectRealizedCostByWorkflowId]);

  const filteredProjectsAverageRealizedCost =
    canViewCosts && filteredProjects.length > 0
      ? filteredProjectsTotalRealizedCost / filteredProjects.length
      : 0;

  const filteredProjectsTotalRemainingEstimatedCost = useMemo(() => {
    if (!canViewCosts || currentAverageHourlyRate === null) return null;
    let total = 0;
    filteredProjects.forEach((project: any) => {
      const formData =
        typeof project.workflowContainerFormData === "string"
          ? JSON.parse(project.workflowContainerFormData || "{}")
          : project.workflowContainerFormData || {};
      const plannedHours = getPlannedHours(formData);
      const spentHours = Number(project.totalSpentHours ?? 0);
      const remainingHours =
        plannedHours > 0 ? Math.max(plannedHours - spentHours, 0) : 0;
      total += remainingHours * currentAverageHourlyRate;
    });
    return total;
  }, [canViewCosts, currentAverageHourlyRate, filteredProjects, getPlannedHours]);

  const filteredProjectsAverageRemainingEstimatedCost =
    canViewCosts &&
    filteredProjects.length > 0 &&
    filteredProjectsTotalRemainingEstimatedCost !== null
      ? filteredProjectsTotalRemainingEstimatedCost / filteredProjects.length
      : null;

  const filteredProjectsTotalEstimatedCost =
    canViewCosts && filteredProjectsTotalRemainingEstimatedCost !== null
      ? filteredProjectsTotalRealizedCost +
        filteredProjectsTotalRemainingEstimatedCost
      : null;

  const filteredProjectsAverageEstimatedCost =
    canViewCosts &&
    filteredProjects.length > 0 &&
    filteredProjectsTotalEstimatedCost !== null
      ? filteredProjectsTotalEstimatedCost / filteredProjects.length
      : null;

  const invoiceCollectionSummaryTry = useMemo(() => {
    if (!canViewCosts) {
      return {
        invoiceTotal: null as number | null,
        totalCollected: null as number | null,
        remainingCollection: null as number | null,
      };
    }

    let invoiceTotal = 0;
    let totalCollected = 0;
    let remainingCollection = 0;
    let hasValue = false;
    let hasMissingConversion = false;

    filteredProjects.forEach((project: any) => {
      let formData: Record<string, any> = {};
      try {
        formData =
          typeof project.workflowContainerFormData === "string"
            ? JSON.parse(project.workflowContainerFormData || "{}")
            : project.workflowContainerFormData || {};
      } catch {
        formData = {};
      }

      const plannedHours = getPlannedHours(formData);
      const spentHours = Number(project.totalSpentHours ?? 0);
      const remainingHours =
        plannedHours > 0 ? Math.max(plannedHours - spentHours, 0) : 0;
      const realizedCostTry = projectRealizedCostByWorkflowId.get(project.id) ?? 0;
      const remainingEstimatedCostTry =
        currentAverageHourlyRate !== null
          ? remainingHours * currentAverageHourlyRate
          : null;
      const totalCollectedTry =
        collectionTotalsTryByWorkflowInstanceId.get(Number(project.id)) ?? null;

      const financeSummary = getProjectFinanceSummaryForDashboard(
        formData,
        realizedCostTry,
        remainingEstimatedCostTry !== null
          ? realizedCostTry + remainingEstimatedCostTry
          : null,
        totalCollectedTry,
      );

      const normalizedCurrency = financeSummary.projectCurrency;
      const normalizedInvoiceTotal =
        financeSummary.invoiceTotal !== null
          ? convertAmountToTry(financeSummary.invoiceTotal, normalizedCurrency)
          : null;
      const normalizedCollected =
        financeSummary.totalCollected !== null
          ? convertAmountToTry(financeSummary.totalCollected, normalizedCurrency)
          : null;
      const normalizedRemaining =
        financeSummary.remainingCollection !== null
          ? convertAmountToTry(
              financeSummary.remainingCollection,
              normalizedCurrency,
            )
          : null;

      if (
        (financeSummary.invoiceTotal !== null && normalizedInvoiceTotal === null) ||
        (financeSummary.totalCollected !== null && normalizedCollected === null) ||
        (financeSummary.remainingCollection !== null && normalizedRemaining === null)
      ) {
        hasMissingConversion = true;
        return;
      }

      if (normalizedInvoiceTotal !== null) {
        invoiceTotal += normalizedInvoiceTotal;
        hasValue = true;
      }
      if (normalizedCollected !== null) {
        totalCollected += normalizedCollected;
      }
      if (normalizedRemaining !== null) {
        remainingCollection += normalizedRemaining;
      }
    });

    if (!hasValue || hasMissingConversion) {
      return {
        invoiceTotal: null,
        totalCollected: null,
        remainingCollection: null,
      };
    }

    return {
      invoiceTotal,
      totalCollected,
      remainingCollection,
    };
  }, [
    canViewCosts,
    collectionTotalsTryByWorkflowInstanceId,
    convertAmountToTry,
    currentAverageHourlyRate,
    filteredProjects,
    getPlannedHours,
    getProjectFinanceSummaryForDashboard,
    projectRealizedCostByWorkflowId,
  ]);

  const filteredProjectsTotalInvoiceTotal = invoiceCollectionSummaryTry.invoiceTotal;
  const filteredProjectsTotalCollectedAmount =
    invoiceCollectionSummaryTry.totalCollected;
  const filteredProjectsTotalRemainingCollection =
    invoiceCollectionSummaryTry.remainingCollection;

  const filteredProjectsAverageInvoiceTotal =
    canViewCosts &&
    filteredProjects.length > 0 &&
    filteredProjectsTotalInvoiceTotal !== null
      ? filteredProjectsTotalInvoiceTotal / filteredProjects.length
      : null;
  const filteredProjectsAverageCollectedAmount =
    canViewCosts &&
    filteredProjects.length > 0 &&
    filteredProjectsTotalCollectedAmount !== null
      ? filteredProjectsTotalCollectedAmount / filteredProjects.length
      : null;
  const filteredProjectsAverageRemainingCollection =
    canViewCosts &&
    filteredProjects.length > 0 &&
    filteredProjectsTotalRemainingCollection !== null
      ? filteredProjectsTotalRemainingCollection / filteredProjects.length
      : null;

  const profitSummaryTry = useMemo(() => {
    if (!canViewCosts) {
      return {
        netProfit: null as number | null,
        estimatedNetProfit: null as number | null,
      };
    }

    let netProfit = 0;
    let estimatedNetProfit = 0;
    let hasValue = false;
    let hasMissingConversion = false;

    filteredProjects.forEach((project: any) => {
      let formData: Record<string, any> = {};
      try {
        formData =
          typeof project.workflowContainerFormData === "string"
            ? JSON.parse(project.workflowContainerFormData || "{}")
            : project.workflowContainerFormData || {};
      } catch {
        formData = {};
      }

      const plannedHours = getPlannedHours(formData);
      const spentHours = Number(project.totalSpentHours ?? 0);
      const remainingHours =
        plannedHours > 0 ? Math.max(plannedHours - spentHours, 0) : 0;
      const realizedCostTry = projectRealizedCostByWorkflowId.get(project.id) ?? 0;
      const remainingEstimatedCostTry =
        currentAverageHourlyRate !== null
          ? remainingHours * currentAverageHourlyRate
          : null;
      const totalCollectedTry =
        collectionTotalsTryByWorkflowInstanceId.get(Number(project.id)) ?? null;

      const financeSummary = getProjectFinanceSummaryForDashboard(
        formData,
        realizedCostTry,
        remainingEstimatedCostTry !== null
          ? realizedCostTry + remainingEstimatedCostTry
          : null,
        totalCollectedTry,
      );

      const normalizedCurrency = financeSummary.projectCurrency;
      const normalizedNetProfit =
        financeSummary.netProfit !== null
          ? convertAmountToTry(financeSummary.netProfit, normalizedCurrency)
          : null;
      const normalizedEstimatedNetProfit =
        financeSummary.estimatedNetProfit !== null
          ? convertAmountToTry(
              financeSummary.estimatedNetProfit,
              normalizedCurrency,
            )
          : null;

      if (
        (financeSummary.netProfit !== null && normalizedNetProfit === null) ||
        (financeSummary.estimatedNetProfit !== null &&
          normalizedEstimatedNetProfit === null)
      ) {
        hasMissingConversion = true;
        return;
      }

      if (normalizedNetProfit !== null) {
        netProfit += normalizedNetProfit;
        hasValue = true;
      }
      if (normalizedEstimatedNetProfit !== null) {
        estimatedNetProfit += normalizedEstimatedNetProfit;
        hasValue = true;
      }
    });

    if (!hasValue || hasMissingConversion) {
      return {
        netProfit: null,
        estimatedNetProfit: null,
      };
    }

    return {
      netProfit,
      estimatedNetProfit,
    };
  }, [
    canViewCosts,
    collectionTotalsTryByWorkflowInstanceId,
    convertAmountToTry,
    currentAverageHourlyRate,
    filteredProjects,
    getPlannedHours,
    getProjectFinanceSummaryForDashboard,
    projectRealizedCostByWorkflowId,
  ]);

  const filteredProjectsTotalNetProfit = profitSummaryTry.netProfit;
  const filteredProjectsTotalEstimatedNetProfit =
    profitSummaryTry.estimatedNetProfit;

  const filteredProjectsAverageNetProfit =
    canViewCosts &&
    filteredProjects.length > 0 &&
    filteredProjectsTotalNetProfit !== null
      ? filteredProjectsTotalNetProfit / filteredProjects.length
      : null;
  const filteredProjectsAverageEstimatedNetProfit =
    canViewCosts &&
    filteredProjects.length > 0 &&
    filteredProjectsTotalEstimatedNetProfit !== null
      ? filteredProjectsTotalEstimatedNetProfit / filteredProjects.length
      : null;

  const uniqueProjectWorkflows = useMemo(() => {
    const workflows = new Set<string>();
    projects.forEach((project: any) => {
      if (project.templateWorkflowName) {
        workflows.add(project.templateWorkflowName);
      }
    });
    return Array.from(workflows).sort();
  }, [projects]);

  const uniqueClients = useMemo(() => {
    const clients = new Set<string>();
    projects.forEach((project: any) => {
      const formData =
        typeof project.workflowContainerFormData === "string"
          ? JSON.parse(project.workflowContainerFormData || "{}")
          : project.workflowContainerFormData || {};
      const clientName = formData.clientName || formData.client;
      if (clientName) clients.add(clientName);
    });
    return Array.from(clients).sort();
  }, [projects]);

  const uniqueManagers = useMemo(() => {
    const managers = new Set<string>();
    projects.forEach((project: any) => {
      const formData =
        typeof project.workflowContainerFormData === "string"
          ? JSON.parse(project.workflowContainerFormData || "{}")
          : project.workflowContainerFormData || {};
      const manager = users.find((u: any) => u.id === formData.projectManager);
      if (manager) {
        managers.add(`${manager.firstName} ${manager.lastName}`);
      }
    });
    return Array.from(managers).sort();
  }, [projects, users]);

  const clearProjectRowFilters = () => {
    setProjectRowFilters(INITIAL_PROJECT_ROW_FILTERS);
  };

  const clearProjectFilters = () => {
    setProjectWorkflowFilter("all");
    setProjectArchiveFilter("all");
    setProjectClientFilter("all");
    setProjectManagerFilter("all");
    setProjectStartDate("");
    setProjectEndDate("");
    clearProjectRowFilters();
  };

  const hasActiveProjectRowFilters = Object.values(projectRowFilters).some(
    (value) => String(value).trim() !== "",
  );
  const hasActiveProjectFilters =
    projectWorkflowFilter !== "all" ||
    projectArchiveFilter !== "all" ||
    projectClientFilter !== "all" ||
    projectManagerFilter !== "all" ||
    projectStartDate !== "" ||
    projectEndDate !== "" ||
    projectSearchTerm !== "" ||
    hasActiveProjectRowFilters;

  const exportProjectsToExcel = () => {
    if (!filteredProjects.length) return;

    const headers: Array<string> = [
      "Proje Adı",
      t("projects:projectCode"),
      t("dashboard:createdAt"),
      workflowText,
      "Müşteri/İşveren",
      "Proje Sorumlusu",
      "Başlangıç Tarihi",
      "Bitiş Tarihi",
      "Gerçekleşen Saat",
      "Hedef Saat",
      "Hedeften Kalan Saat",
    ];

    if (canViewCosts) {
      headers.push(
        "İşin Tutarı",
        "KDV",
        t(
          "projects:workflowInstanceDetail.finance.invoiceTotal",
          "Toplam Tahsil Edilecek Tutar",
        ),
        t(
          "projects:workflowInstanceDetail.finance.totalCollection",
          "Tahsil Edilen",
        ),
        t(
          "projects:workflowInstanceDetail.finance.remainingCollection",
          "Kalan Tahsilat",
        ),
        t("dashboard:managerProjects.realizedCost"),
        "Kalan Maliyet Tahmini",
        t(
          "dashboard:managerProjects.estimatedTotalCost",
          "Tahmini Toplam Maliyet",
        ),
        t(
          "projects:workflowInstanceDetail.finance.netProfit",
          "Gerçekleşen Net Kâr",
        ),
        t(
          "projects:workflowInstanceDetail.finance.estimatedNetProfit",
          "Toplam Tahmini Net Kâr",
        ),
      );
    }

    const rows: Array<Array<string | number>> = [headers];
    filteredProjects.forEach((project: any) => {
      const formData =
        typeof project.workflowContainerFormData === "string"
          ? JSON.parse(project.workflowContainerFormData || "{}")
          : project.workflowContainerFormData || {};
      const manager = users.find((u: any) => u.id === formData.projectManager);
      const managerName = manager
        ? `${manager.firstName} ${manager.lastName}`
        : "-";
      const spentHours = Number(project.totalSpentHours ?? 0);
      const plannedHours = getPlannedHours(formData);
      const remainingHours =
        Number.isFinite(plannedHours) && plannedHours > 0
          ? plannedHours - (Number.isFinite(spentHours) ? spentHours : 0)
          : null;

      const row: Array<string | number> = [
        formData.name || formData.projectName || "İsimsiz Proje",
        getProjectCode(project, formData) || "-",
        project.createdAt ? new Date(project.createdAt).toLocaleDateString("tr-TR") : "-",
        project.templateWorkflowName || "-",
        formData.clientName || formData.client || "-",
        managerName,
        formData.startDate ? new Date(formData.startDate).toLocaleDateString("tr-TR") : "-",
        formData.endDate ? new Date(formData.endDate).toLocaleDateString("tr-TR") : "-",
        Number.isFinite(spentHours) ? Number(spentHours.toFixed(2)) : "-",
        Number.isFinite(plannedHours) && plannedHours > 0
          ? Number(plannedHours.toFixed(2))
          : "-",
        remainingHours !== null ? Number(remainingHours.toFixed(2)) : "-",
      ];

      if (canViewCosts) {
        const projectCurrency = getProjectCurrencyForFormData(formData);
        const realizedCostTry = projectRealizedCostByWorkflowId.get(project.id) ?? 0;
        const remainingEstimatedCostTry =
          currentAverageHourlyRate !== null &&
          Number.isFinite(plannedHours) &&
          plannedHours > 0
            ? Math.max(plannedHours - (Number.isFinite(spentHours) ? spentHours : 0), 0) *
              currentAverageHourlyRate
            : null;
        const totalCollectedTry =
          collectionTotalsTryByWorkflowInstanceId.get(Number(project.id)) ?? null;
        const realizedCost = convertTryAmountToCurrency(
          realizedCostTry,
          projectCurrency,
        );
        const remainingEstimatedCost =
          remainingEstimatedCostTry !== null
            ? convertTryAmountToCurrency(remainingEstimatedCostTry, projectCurrency)
            : null;
        const totalEstimatedCost =
          realizedCost !== null && remainingEstimatedCost !== null
            ? realizedCost + remainingEstimatedCost
            : null;
        const financeSummary = getProjectFinanceSummaryForDashboard(
          formData,
          realizedCostTry,
          remainingEstimatedCostTry !== null
            ? realizedCostTry + remainingEstimatedCostTry
            : null,
          totalCollectedTry,
        );
        const vatAmount =
          financeSummary.projectAmount !== null &&
          financeSummary.invoiceTotal !== null
            ? financeSummary.invoiceTotal - financeSummary.projectAmount
            : null;
        const formatExportMoney = (value: number | null | undefined) =>
          value !== null && value !== undefined && Number.isFinite(value)
            ? `${value.toFixed(2)} ${projectCurrency}`
            : "-";

        row.push(
          formatExportMoney(financeSummary.projectAmount),
          formatExportMoney(vatAmount),
          formatExportMoney(financeSummary.invoiceTotal),
          formatExportMoney(financeSummary.totalCollected),
          formatExportMoney(financeSummary.remainingCollection),
          formatExportMoney(realizedCost),
          formatExportMoney(remainingEstimatedCost),
          formatExportMoney(totalEstimatedCost),
          formatExportMoney(financeSummary.netProfit),
          formatExportMoney(financeSummary.estimatedNetProfit),
        );
      }
      rows.push(row);
    });

    const summaryRow = new Array(headers.length).fill("");
    summaryRow[0] =
      projectSummaryMode === "average"
        ? "Ortalama (Filtre Sonucu)"
        : "Toplam (Filtre Sonucu)";
    summaryRow[1] = `${filteredProjects.length} kayıt`;
    summaryRow[8] = formatHoursTr(
      projectSummaryMode === "average"
        ? filteredProjectsAverageSpentDuration
        : filteredProjectsTotalSpentDuration,
    );
    summaryRow[9] = formatHoursTr(
      projectSummaryMode === "average"
        ? filteredProjectsAveragePlannedDuration
        : filteredProjectsTotalPlannedDuration,
    );
    summaryRow[10] = formatHoursTr(
      projectSummaryMode === "average"
        ? filteredProjectsAverageRemainingDuration
        : filteredProjectsTotalRemainingDuration,
    );
    rows.push(summaryRow);

    exportRowsToExcel({
      fileName: `Projeler_${new Date().toISOString().slice(0, 10)}.xlsx`,
      sheetName: "Projeler",
      rows,
    });
  };

  return {
    projectSearchTerm,
    setProjectSearchTerm,
    projectWorkflowFilter,
    setProjectWorkflowFilter,
    projectArchiveFilter,
    setProjectArchiveFilter,
    projectClientFilter,
    setProjectClientFilter,
    projectManagerFilter,
    setProjectManagerFilter,
    projectStartDate,
    setProjectStartDate,
    projectEndDate,
    setProjectEndDate,
    projectRowFilters,
    setProjectRowFilters,
    projectCurrentPage,
    setProjectCurrentPage,
    projectItemsPerPage,
    setProjectItemsPerPage,
    projectSummaryMode,
    setProjectSummaryMode,
    projectSortField,
    projectSortOrder,
    handleProjectSort,
    filteredProjects,
    paginatedProjects,
    projectTotalPages,
    currentAverageHourlyRate,
    collectionTotalsTryByWorkflowInstanceId,
    filteredProjectsAverageSpentDuration,
    filteredProjectsTotalSpentDuration,
    filteredProjectsAveragePlannedDuration,
    filteredProjectsTotalPlannedDuration,
    filteredProjectsAverageRealizedCost,
    filteredProjectsTotalRealizedCost,
    filteredProjectsAverageInvoiceTotal,
    filteredProjectsTotalInvoiceTotal,
    filteredProjectsAverageCollectedAmount,
    filteredProjectsTotalCollectedAmount,
    filteredProjectsAverageRemainingCollection,
    filteredProjectsTotalRemainingCollection,
    filteredProjectsAverageNetProfit,
    filteredProjectsTotalNetProfit,
    filteredProjectsAverageEstimatedNetProfit,
    filteredProjectsTotalEstimatedNetProfit,
    filteredProjectsAverageRemainingEstimatedCost,
    filteredProjectsTotalRemainingEstimatedCost,
    filteredProjectsAverageEstimatedCost,
    filteredProjectsTotalEstimatedCost,
    uniqueProjectWorkflows,
    uniqueClients,
    uniqueManagers,
    clearProjectFilters,
    clearProjectRowFilters,
    hasActiveProjectFilters,
    exportProjectsToExcel,
  };
};
